import React from "react";
import styled from "styled-components";
import { Cross } from "@styled-icons/entypo/Cross";
import { Folder } from "styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Modal from "components/Modal";
import Icon from "components/Icon";
import facebookSelectors from "modules/facebook/selectors";

const FileDetailModal = ({
  show,
  setShow,
  fileName,
  folderType,
  folderName,
  folderCount,
  folderUrl,
  updatedDate,
  updatedByImage,
  updatedBy,
}) => {
  const [t] = useTranslation();
  const sheetsModifiedData = useSelector((state) => facebookSelectors.updateSheetsModifiedDataResult(state));
  const fileInfoErrorResult = useSelector((state) => facebookSelectors.setFileInfoErrorResult(state));

  const handleCloseModal = () => {
    setShow("hide");
  };

  const FileData = [
    { key: "file-name", label: t("__file_name"), value: fileName, img: null },
    { key: "file-edit-date", label: t("__file_updated_date"), value: updatedDate, img: null },
    { key: "file-edit-by", label: t("__file_updated_by"), value: updatedBy, img: updatedByImage },
  ];

  const generateRootFolder = (type) => {
    const folderRoot = {
      myDrive: t("__my_drive"),
      sharedDrive: t("__shared_drive"),
      sharedWithMe: t("__shared_with_me"),
    };

    return folderRoot[type];
  };

  const FolderLink = ({ folderCount, folderType, folderUrl, folderName }) => (
    <a
      href={folderUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={folderCount === 0 && folderType === "sharedWithMe" ? "pointer-none" : ""}
    >
      <FolderNameStyle type="folder" folderCount={folderCount} folderType={folderType}>
        {folderCount === 0 && folderType === "sharedWithMe" ? (
          <>
            <StyleFolder size="16px" />
            {t("__shared_with_me")}
          </>
        ) : (
          <>
            {folderCount === 0 && <StyleFolder size="16px" />}
            <div className={folderCount > 1 ? "folder-name-more" : folderCount > 0 ? "folder-name-one" : "folder-name"}>
              {folderName}
            </div>
          </>
        )}
      </FolderNameStyle>
    </a>
  );

  const FolderRoot = ({ folderCount, folderType }) =>
    folderCount > 0 && (
      <>
        <FolderNameStyle type="root" folderCount={folderCount} folderType={folderType}>
          <StyleFolder size="16px" />
          <span className="root-name">{generateRootFolder(folderType)}</span>
        </FolderNameStyle>
        <NextImage src="/images/next.svg" />
      </>
    );

  const FolderDots = ({ folderCount }) =>
    folderCount > 1 && (
      <>
        <Icon className="three-dot" name="icon-dot-3" size="12px" color="#555" />
        <NextImage src="/images/next.svg" />
      </>
    );

  const FileDetailContent = () => {
    return (
      <FileModalContentContainer>
        <StyledContainer>
          <StyledCrossIcon onClick={handleCloseModal} />
          <FileMainContainer>
            <Title>{t("__fbleads_google_sheets_file_detail")}</Title>
            {Object.keys(sheetsModifiedData.data.updatedData).length !== 0 ? (
              <Content>
                {FileData.map((item, index) => (
                  <DetailContainer key={index}>
                    <DetailLabel>{item.label}</DetailLabel>

                    <DetailValue>
                      {item.img != null && item.img !== "folder" && <DetailImage src={item.img} />}
                      {item.key === "file-position" ? (
                        <FolderPath>
                          {folderType === "sharedWithMe" ? (
                            <>
                              <FolderRoot folderCount={folderCount} folderType={folderType} />
                              <FolderDots folderCount={folderCount} />
                              <FolderLink
                                folderCount={folderCount}
                                folderType={folderType}
                                folderUrl={folderUrl}
                                folderName={folderName}
                              />
                            </>
                          ) : (
                            <>
                              <FolderRoot folderCount={folderCount - 1} folderType={folderType} />
                              <FolderDots folderCount={folderCount - 1} />
                              <FolderLink
                                folderCount={folderCount - 1}
                                folderType={folderType}
                                folderUrl={folderUrl}
                                folderName={folderName}
                              />
                            </>
                          )}
                        </FolderPath>
                      ) : (
                        <DetailText>{item.value}</DetailText>
                      )}
                    </DetailValue>
                  </DetailContainer>
                ))}
              </Content>
            ) : fileInfoErrorResult.data.fileInfoError ? (
              <FileInfoErrorArea>
                <FileInfoErrorMessage>{t("__file_info_error")}</FileInfoErrorMessage>
              </FileInfoErrorArea>
            ) : (
              <LoadingArea>
                <StyledLoadingIcon></StyledLoadingIcon>
              </LoadingArea>
            )}
          </FileMainContainer>
        </StyledContainer>
      </FileModalContentContainer>
    );
  };
  return (
    <>
      <Modal state={show} content={FileDetailContent()} maxWidth="500px !important" />
    </>
  );
};

export default FileDetailModal;

const FileModalContentContainer = styled.div`
  margin-bottom: 20px;
  color: #575757;
`;

const StyledContainer = styled.div`
  font-size: 14px;
  color: #555;
  position: relative;

  .m-b-10 {
    margin-bottom: 10px;
  }

  @media screen and (max-width: 834px) {
    .links-container {
      display: flex;
      justify-content: center;
    }
  }
`;

const StyledCrossIcon = styled(Cross)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
  width: 40px;
  cursor: pointer;
`;

const FileMainContainer = styled.div`
  display: flex;
  background-color: #fbfeff;
  border-radius: 10px;
  border: 1px solid #f5f9fc;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  color: #575757;
  margin-bottom: 5px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  line-height: 2.5em;
  overflow-x: auto;
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const DetailLabel = styled.div`
  width: 110px;
  padding-right: 20px;
`;

const DetailValue = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  a {
    text-decoration: none;
    color: #555;
  }
`;

const FolderPath = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  .three-dot {
    margin: 0px 10px;
  }
  .pointer-none {
    pointer-events: none;
  }
`;

const FolderNameStyle = styled.div.attrs((props) => ({
  backgroundColor: props.type === "folder" ? "#f9f9f9" : "#fff",
  borderRadius: props.type === "folder" ? "6px" : "0px",
  padding: props.type === "root" ? "0px 10px 0px 0px" : "0px 10px",
  left:
    (props.folderCount === 0 && props.folderType !== "sharedWithMe") ||
    (props.folderCount === 0 && props.folderType === "sharedWithMe")
      ? "-10px"
      : "0px",
}))`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.padding};
  position: relative;
  left: ${(props) => props.left};

  .folder-name {
    white-space: nowrap;
  }

  .folder-name-one {
    white-space: nowrap;
  }

  .folder-name-more {
    white-space: nowrap;
  }

  .root-name {
    white-space: nowrap;
  }

  &:hover {
    background-color: ${(props) => props.backgroundColor};
    border-radius: ${(props) => props.borderRadius};
  }
`;

const StyleFolder = styled(Folder)`
  color: #aaa;
  width: 16px;
  height: 16px;
  margin-right: 5px;
`;

const NextImage = styled.img`
  width: 16px;
  height: 16px;
  margin: 0 10px;
`;

const DetailImage = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 5px;
  border-radius: 50%;
`;

const DetailText = styled.div`
  white-space: nowrap;
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 150px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const FileInfoErrorArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .cancel-button {
    margin: 0;
  }
`;

const FileInfoErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 150px;

  .edit-connection {
    text-decoration: underline;
    cursor: pointer;
  }
`;
